import { containsIgnoreCase } from "../../utils/stringUtils";
import { formatYmd, parseLooseYmd, todayYmd } from "../../features/cases/dates";
import { getSelectedDiadikasia } from "../../features/cases/caseFilters";
import { OUT_ACTIONS } from "../../features/cases/listActions";
import { extraOf } from "../../features/outsolon/outsolonCase";
import OutSolon from "../../components/OutSolon/OutSolon";
import {
  DIADIKASIA_LABELS,
  Ellipsis,
  formatServerDate,
  parseServerDate,
} from "../cases/caseColumns";
import UpcomingOverview from "../cases/UpcomingOverview";
import OutSolonPanelBody from "./OutSolonPanelBody";

const courtLabel = (c) => [c?.dikastirio, c?.topos].filter(Boolean).join(" ");
const dikasimosYmd = (c) => parseLooseYmd(extraOf(c).dikasimos);
const apotelesma = (c) =>
  c.apotelesma?.length > 2 ? c.apotelesma : extraOf(c).apotelesma?.trim() || "";

const dateColumn = (id, label, getYmd, { upcoming = false } = {}) => ({
  id,
  label,
  width: 100,
  defaultVisible: true,
  sortValue: getYmd,
  render: (c) => {
    const ymd = getYmd(c);
    if (!ymd) return null;
    const className = upcoming ? (ymd >= todayYmd() ? "ds-date-upcoming" : "ds-muted") : "";
    return <span className={className}>{formatYmd(ymd)}</span>;
  },
});

const extraText = (key) => ({
  sortValue: (c) => extraOf(c)[key]?.trim?.() || "",
  render: (c) => <Ellipsis>{extraOf(c)[key]?.trim?.() || ""}</Ellipsis>,
});

const COLUMNS = [
  {
    id: "note",
    flex: true,
    label: "Σημείωση",
    width: 220,
    defaultVisible: true,
    sortValue: (c) => c.note?.trim() || "",
    render: (c) => <Ellipsis>{c.note?.trim() || ""}</Ellipsis>,
  },
  {
    id: "court",
    flex: true,
    label: "Δικαστήριο",
    width: 170,
    defaultVisible: true,
    sortValue: courtLabel,
    render: (c) => <Ellipsis>{courtLabel(c)}</Ellipsis>,
  },
  {
    id: "diadikasia",
    flex: true,
    label: "Διαδικασία",
    width: 160,
    defaultVisible: true,
    sortValue: (c) => c.diadikasia || "",
    render: (c) => <Ellipsis>{c.diadikasia || ""}</Ellipsis>,
  },
  dateColumn("katathesi", "Κατάθεση", (c) => parseLooseYmd(c.katathesi)),
  dateColumn("dikasimos", "Δικάσιμος", dikasimosYmd, { upcoming: true }),
  {
    id: "apotelesma",
    flex: true,
    label: "Αποτέλεσμα",
    width: 180,
    defaultVisible: true,
    sortValue: apotelesma,
    render: (c) => <Ellipsis>{apotelesma(c)}</Ellipsis>,
  },
  { id: "antikeimeno", flex: true, label: "Αντικείμενο", width: 200, defaultVisible: false, ...extraText("antikeimeno") },
  { id: "eidos", flex: true, label: "Είδος", width: 160, defaultVisible: false, ...extraText("eidos") },
  {
    id: "selected_diadikasia",
    flex: true,
    label: "Επιλ. διαδικασία",
    width: 150,
    defaultVisible: false,
    sortValue: (c) => DIADIKASIA_LABELS[getSelectedDiadikasia(c)] || "",
    render: (c) => <Ellipsis>{DIADIKASIA_LABELS[getSelectedDiadikasia(c)] || ""}</Ellipsis>,
  },
  {
    id: "created",
    label: "Καταχώρηση",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => parseServerDate(c.created_at)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.created_at)}</span>,
  },
];

const search = (c, q) =>
  containsIgnoreCase(c.note, q) ||
  containsIgnoreCase(c.topos, q) ||
  containsIgnoreCase(c.dikastirio, q) ||
  containsIgnoreCase(c.diadikasia, q);

const FILTER_KEYS = ["dikastirio", "diadikasia"];

const uniqueSorted = (values) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, "el"));

const getFilterDefs = (rows) => [
  {
    key: "dikastirio",
    label: "Δικαστήριο",
    allLabel: "Όλα",
    options: uniqueSorted(rows.map((c) => c.dikastirio)).map((d) => ({ value: d, label: d })),
  },
  {
    key: "diadikasia",
    label: "Διαδικασία",
    options: uniqueSorted(rows.map((c) => c.diadikasia)).map((d) => ({ value: d, label: d })),
  },
];

const applyFilters = (rows, { dikastirio, diadikasia }) =>
  rows.filter(
    (c) =>
      (!dikastirio || c.dikastirio === dikastirio) &&
      (!diadikasia || c.diadikasia === diadikasia)
  );

const title = (c) => (c?.note?.trim() ? c.note : courtLabel(c));
const subtitle = (c) =>
  c ? [c.note?.trim() ? courtLabel(c) : null, c.diadikasia].filter(Boolean).join(" · ") : "";

const getEvents = (c) => {
  const ymd = dikasimosYmd(c);
  return ymd ? [{ ymd }] : [];
};

const Overview = ({ rows, onOpen }) => (
  <UpcomingOverview
    rows={rows}
    getEvents={getEvents}
    getLabel={(c) => ({ primary: title(c), secondary: subtitle(c) })}
    onOpen={onOpen}
    title="Επερχόμενες δικάσιμοι"
    emptyText="Δεν υπάρχουν επερχόμενες δικάσιμοι. Τη δικάσιμο τη συμπληρώνετε εσείς στην καρτέλα της υπόθεσης."
    stats={[
      { label: "υποθέσεις", value: rows.length },
      { label: "με δικάσιμο", value: rows.filter((c) => dikasimosYmd(c)).length },
    ]}
  />
);

const AddForm = ({ onClose, onAdded }) => (
  <OutSolon handleClickAddCaseModal={onClose} handleAddCase={onAdded} />
);

export const OUTSOLON_CONFIG = {
  scope: "outsolon",
  title: "Αστικά (Εκτός Σόλων)",
  noun: { one: "υπόθεση", many: "υποθέσεις", this: "αυτή την υπόθεση" },
  searchPlaceholder: "Αναζήτηση (σημείωση, δικαστήριο, διαδικασία)",
  addLabel: "Νέα υπόθεση",
  columns: COLUMNS,
  columnsById: Object.fromEntries(COLUMNS.map((c) => [c.id, c])),
  getRowLabel: title,
  renderStatus: null,
  search,
  filterKeys: FILTER_KEYS,
  getFilterDefs,
  applyFilters,
  actions: OUT_ACTIONS,
  noteInPanel: true,
  detail: {
    endpoints: { item: (id) => `/outsolon/${id}` },
    fullPageHref: (id) => `/outsolon/${id}`,
    getTitle: title,
    getSubtitle: subtitle,
    getRefreshKey: (c) => c?.note ?? "",
    documentTitle: (c) => `${title(c)} | ${courtLabel(c)} | MySolon.gr`,
    renderBody: ({ data, setData, updateRow }) => (
      <OutSolonPanelBody data={data} setData={setData} updateRow={updateRow} />
    ),
  },
  Overview,
  AddForm,
  afterAdd: { insert: true, refreshDelays: [0] },
};
